import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import supabase from '../lib/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Generate a simple JWT-like token (in production, use proper JWT library)
  const generateToken = (userData) => {
    const payload = {
      user: userData,
      exp: Date.now() + (7 * 24 * 60 * 60 * 1000), // 7 days from now
      iat: Date.now()
    };
    return btoa(JSON.stringify(payload)); // Base64 encode (simplified)
  };

  // Verify token and extract user data
  const verifyToken = (token) => {
    try {
      const payload = JSON.parse(atob(token));
      if (payload.exp < Date.now()) {
        // Token expired
        return null;
      }
      return payload.user;
    } catch (error) {
      console.error('Token verification failed:', error);
      return null;
    }
  };

  useEffect(() => {
    // Check for stored session token
    const token = localStorage.getItem('how2apply_token');
    const sessionExpiry = localStorage.getItem('how2apply_session_expiry');

    if (token && sessionExpiry) {
      const expiryTime = parseInt(sessionExpiry);
      if (Date.now() < expiryTime) {
        // Session is still valid
        const userData = verifyToken(token);
        if (userData) {
          setUser(userData);
        } else {
          // Invalid token, clear storage
          clearSession();
        }
      } else {
        // Session expired, clear storage
        clearSession();
        toast.info('Your session has expired. Please log in again.');
      }
    }
    setLoading(false);
  }, []);

  const clearSession = () => {
    localStorage.removeItem('how2apply_token');
    localStorage.removeItem('how2apply_session_expiry');
    localStorage.removeItem('how2apply_user');
    setUser(null);
  };

  // Ensure user exists in Supabase domain tables based on role
  const ensureUserRecords = async (incomingUser) => {
    try {
      const safeName = incomingUser.name || 'User';
      const safeEmail = incomingUser.email || null;
      const safePhone = incomingUser.mobile || null;
      const safeId = incomingUser.id;
      
      console.log('Ensuring user records for:', { safeName, safeEmail, safePhone, safeId, role: incomingUser.role });

      if (incomingUser.role === 'admin') {
        // Admin users don't need database records for demo mode
        console.log('Admin user - skipping database record creation');
        return true;
      } else if (incomingUser.role === 'operator') {
        // Ensure operator record exists
        try {
          const { data, error } = await supabase
            .from('operators_a7b5c9')
            .select('id, status')
            .eq('user_id', safeId)
            .maybeSingle();
            
          console.log('Operator lookup result:', { data, error });
          
          if (error && error.code !== 'PGRST116') {
            console.warn('Operator lookup failed (non-blocking):', error);
            return true; // Don't block login
          }
          
          if (!data) {
            console.log('Creating new operator record');
            const { error: insertError } = await supabase
              .from('operators_a7b5c9')
              .insert({
                user_id: safeId,
                full_name: safeName,
                email: safeEmail,
                phone: safePhone,
                status: 'active',
                operator_code: `OP${Date.now().toString(36).toUpperCase()}`
              });
            if (insertError) {
              console.warn('Error creating operator record (non-blocking):', insertError);
              return true; // Don't block login
            }
            console.log('Operator record created successfully');
          }
        } catch (opErr) {
          console.warn('Operator record management failed (non-blocking):', opErr);
          return true; // Don't block login
        }
      } else {
        // For customers, the record should already exist from registration
        // Just verify it exists, don't create duplicate
        try {
          const { data, error } = await supabase
            .from('customers_h2a')
            .select('id')
            .eq('user_id', safeId)
            .maybeSingle();
            
          console.log('Customer lookup result:', { data, error });
          
          if (error && error.code !== 'PGRST116') {
            console.warn('Customer lookup failed (non-blocking):', error);
            return true; // Don't block login
          }
          
          if (!data) {
            console.log('Customer record not found, creating new one');
            const { error: cInsErr } = await supabase
              .from('customers_h2a')
              .insert({
                user_id: safeId,
                full_name: safeName,
                email: safeEmail,
                phone: safePhone,
                created_at: new Date().toISOString()
              });
            if (cInsErr) {
              console.warn('Error creating customer record (non-blocking):', cInsErr);
              return true; // Don't block login
            }
            console.log('Customer record created successfully');
          } else {
            console.log('Customer record already exists');
          }
        } catch (custErr) {
          console.warn('Customer record management failed (non-blocking):', custErr);
          return true; // Don't block login
        }
      }
      return true;
    } catch (e) {
      console.error('ensureUserRecords error:', e);
      // Don't block login for database issues
      console.warn('Database setup skipped due to connection issue');
      return true;
    }
  };

  const login = async (userData) => {
    await ensureUserRecords(userData);

    const token = generateToken(userData);
    const expiryTime = Date.now() + (7 * 24 * 60 * 60 * 1000); // 7 days

    // Update the user record in Supabase with the new session token
    if (userData.role === 'operator') {
      await supabase
        .from('operators_a7b5c9')
        .update({ session_token: token })
        .eq('user_id', userData.id);
    } else if (userData.role === 'customer') {
      await supabase
        .from('customers_h2a')
        .update({ session_token: token })
        .eq('user_id', userData.id);
    } else {
      // admin or other roles: skip updating domain tables
    }

    // Also proactively update any available orders created without contact with this user's latest email/phone
    try {
      await supabase
        .from('available_orders_a7b5c9')
        .update({
          customer_email: userData.email || null,
          customer_phone: userData.mobile || null
        })
        .eq('customer_id', userData.id)
        .eq('status', 'available');
    } catch (syncAvailErr) {
      console.warn('Unable to sync available orders contact info:', syncAvailErr);
    }

    // Store token and expiry
    localStorage.setItem('how2apply_token', token);
    localStorage.setItem('how2apply_session_expiry', expiryTime.toString());
    localStorage.setItem('how2apply_user', JSON.stringify(userData));

    setUser(userData);
    toast.success(`Welcome back, ${userData.name}!`);
  };

  const logout = async () => {
    const token = localStorage.getItem('how2apply_token');
    const userData = verifyToken(token);

    if (userData) {
      try {
        if (userData.role === 'operator') {
          await supabase
            .from('operators_a7b5c9')
            .update({ session_token: null })
            .eq('user_id', userData.id);
        } else {
          await supabase
            .from('customers_h2a')
            .update({ session_token: null })
            .eq('user_id', userData.id);
        }
      } catch (error) {
        console.error('Supabase logout update failed:', error);
      }
    }
    
    clearSession();
    toast.success('Logged out successfully');
  };

  const updateUser = (updatedData) => {
    const updatedUser = { ...user, ...updatedData };
    
    // Regenerate token with updated user data
    const token = generateToken(updatedUser);
    const expiryTime = Date.now() + (7 * 24 * 60 * 60 * 1000);
    
    localStorage.setItem('how2apply_token', token);
    localStorage.setItem('how2apply_session_expiry', expiryTime.toString());
    localStorage.setItem('how2apply_user', JSON.stringify(updatedUser));
    
    setUser(updatedUser);
  };

  // Extend session (called on user activity)
  const extendSession = () => {
    if (user) {
      const expiryTime = Date.now() + (7 * 24 * 60 * 60 * 1000);
      localStorage.setItem('how2apply_session_expiry', expiryTime.toString());
    }
  };

  // Check if session is about to expire (within 1 hour)
  const isSessionExpiringSoon = () => {
    const sessionExpiry = localStorage.getItem('how2apply_session_expiry');
    if (sessionExpiry) {
      const expiryTime = parseInt(sessionExpiry);
      const oneHour = 60 * 60 * 1000;
      return (expiryTime - Date.now()) < oneHour;
    }
    return false;
  };

  const value = {
    user,
    login,
    logout,
    updateUser,
    loading,
    isAuthenticated: !!user,
    extendSession,
    isSessionExpiringSoon,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};